import React, { useEffect, useRef } from "react";
import PieChartOutlineIcon from "@mui/icons-material/PieChartOutlined";
import SearchIcon from "@mui/icons-material/Search";

import { useResponsive } from "../../../../../hooks/useResponsive";
import {
  Holding,
  HoldingsSummary,
  taxWithheldOf,
  valuationStatusOf,
} from "../../../../../services/holdings";
import {
  isPricedValuation,
  PriceProvenance,
  priceUpdatedFor,
  VALUATION_PENDING_PLACEHOLDER,
  VALUATION_PLACEHOLDER,
  ValuationAmount,
  valuationAmountText,
  PRE_TAX_GAIN_TOOLTIP,
} from "../../../../../services/valuation";
import { DateFormat, formatCurrency, formatDate } from "../../../../../utils";
import { BREAKPOINTS } from "../../../../../utils/constants";
import { NotAnnualizedInfo } from "../../../NotAnnualizedInfo";
import { InvestmentsDetails } from "../../../types";
import {
  currencyOrDash,
  coverageGapOf,
  irrTextOf,
  irrTooltipOf,
  isPeriodIrr,
  openPositionsCaption,
  presentedValueOf,
  pricedCoverageCaption,
  signedCurrencyOrDash,
} from "../../../utils";
import {
  CONTEXT_RAIL_HEADING_ID,
  CONTEXT_RAIL_PANEL_ID,
  SECTION_CARD_BREAKPOINT,
} from "../../constants";
import { buildHoldingDisplay } from "../Holdings/holdingDisplay";
import { REFRESH_NOTICE_COPY, RefreshNotice } from "../RefreshNotice";
import { SectionEmptyState, SectionSkeletonRows } from "../SectionCard";
import { ButtonTypes } from "../../../../../components/Button";
import { ConcentrationList } from "./ConcentrationList";
import { MetricGrid } from "./MetricGrid";
import { positionMetrics } from "./positionMetrics";
import { ContextMetric, ContextRailProps } from "./types";
import {
  Body,
  Header,
  HeaderText,
  Heading,
  HeadingNote,
  Hero,
  HeroAmount,
  HeroCaption,
  HeroLabel,
  Message,
  Note,
  NoticeInset,
  Panel,
  Rail,
  ResetButton,
} from "./styles";

const ACCOUNT_HEADING = "Account insight";
const ACCOUNT_SUBTITLE = "All open holdings";
const LOADING_HEADING = "Position context";
const LOADING_SUBTITLE = "Updating the holdings workspace";
const ERROR_MESSAGE =
  "Position context is unavailable while the holdings ledger cannot be loaded.";
const EMPTY_ACCOUNT_HEADING = "Account context";
const EMPTY_ACCOUNT_SUBTITLE = "Waiting for the first holding";
const EMPTY_ACCOUNT_TITLE = "Context starts with a trade";
const EMPTY_ACCOUNT_MESSAGE =
  "Position evidence and concentration will appear here after the first trade.";
const NO_RESULTS_HEADING = "No position selected";
const NO_RESULTS_SUBTITLE = "The current filters return no holdings";
const NO_RESULTS_TITLE = "Clear filters to inspect a position";
const NO_RESULTS_MESSAGE =
  "The context rail only shows evidence for positions visible in the workspace.";
const CONCENTRATION_SIZE = 4;
const EVIDENCE_PEER_COUNT = 3;
const CONCENTRATION_NOTE =
  "Concentration is withheld because a complete account value is required for every allocation percentage.";

// The market close the account value covers, not the fetch time; a fetch-only quote keeps the shared wording.
const completeThroughOf = (provenance: PriceProvenance): string | undefined =>
  provenance.providerMarketDate
    ? `complete through ${formatDate(provenance.providerMarketDate, DateFormat.DMY_SPACE)}`
    : priceUpdatedFor(provenance)?.text;

const captionOf = (parts: (string | undefined)[]): string =>
  parts.filter(Boolean).join(" · ");

// A failed or pre-Release-A summary falls back as a whole: mixing legs would pair two ledger versions.
const accountMetrics = (
  investment: InvestmentsDetails,
  summary: HoldingsSummary | null,
): ContextMetric[] => {
  const ledgerFigures = summary ?? {
    cashBalance: investment.cashBalance,
    securitiesValue: investment.securitiesValue,
    brokerCosts: investment.brokerCosts,
    unrealizedReturnValue: null,
    realizedReturnValue: null,
    totalIncome: null,
    withholdingTaxCosts: null,
    interestTaxWithheld: null,
    netIncome: null,
  };
  return [
    { label: "Cash", value: currencyOrDash(ledgerFigures.cashBalance) },
    {
      label: "Securities value",
      value: currencyOrDash(ledgerFigures.securitiesValue),
    },
    {
      label: "Open unrealized",
      value: signedCurrencyOrDash(ledgerFigures.unrealizedReturnValue),
    },
    {
      label: "Lifetime realized",
      value: signedCurrencyOrDash(ledgerFigures.realizedReturnValue),
    },
    { label: "Gross income", value: currencyOrDash(ledgerFigures.totalIncome) },
    {
      label: "Tax withheld",
      value: currencyOrDash(taxWithheldOf(ledgerFigures)),
    },
    { label: "Net received", value: currencyOrDash(ledgerFigures.netIncome) },
    { label: "Costs", value: currencyOrDash(ledgerFigures.brokerCosts) },
    {
      label: "IRR",
      labelHint: isPeriodIrr(investment.isIrrAnnualized) ? (
        <NotAnnualizedInfo />
      ) : undefined,
      value: irrTextOf(investment.irr) ?? VALUATION_PENDING_PLACEHOLDER,
      title: irrTooltipOf(PRE_TAX_GAIN_TOOLTIP, investment.isIrrAnnualized),
      spansRow: true,
    },
  ];
};

const coverageGapMetrics = (summary: HoldingsSummary): ContextMetric[] => [
  { label: "Cash", value: currencyOrDash(summary.cashBalance) },
  {
    label: "Known securities subtotal",
    value: formatCurrency(summary.knownSecuritiesValue),
  },
  {
    label: "Valuation coverage",
    value: `${summary.pricedPositionCount} of ${summary.openPositionCount} priced`,
  },
  { label: "Allocation", value: VALUATION_PLACEHOLDER },
];

// Same source as the legs beneath it, so the hero always equals cash plus securities.
const accountValueOf = (
  investment: InvestmentsDetails,
  summary: HoldingsSummary | null,
): ValuationAmount =>
  summary
    ? { value: summary.brokerValue, valuation: valuationStatusOf(summary) }
    : { value: presentedValueOf(investment), valuation: investment.valuation };

// One unpriced position nulls every allocation in the account, so this is a full list or nothing.
const rankedByShareOf = (holdings: Holding[]): Holding[] | null => {
  const open = holdings.filter((holding) => !holding.isClosed);
  if (open.some((holding) => holding.allocationPercent === null)) {
    return null;
  }
  return open
    .filter((holding) => (holding.allocationPercent ?? 0) > 0)
    .sort((a, b) => (b.allocationPercent ?? 0) - (a.allocationPercent ?? 0));
};

const positionEvidenceOf = (
  selected: Holding,
  ranked: Holding[],
): Holding[] => [
  selected,
  ...ranked
    .filter((holding) => holding.id !== selected.id)
    .slice(0, EVIDENCE_PEER_COUNT),
];

const ContextRailComponent: React.FC<ContextRailProps> = ({
  investment,
  summary,
  selectedHolding,
  onClearSelection,
  holdings,
  counts,
  isLoading,
  hasError,
  refreshFailed,
  isRefreshing,
  onRetry,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const isStacked = useResponsive(BREAKPOINTS.laptopLarge);
  const isMobile = useResponsive(SECTION_CARD_BREAKPOINT);
  // On mobile the selected position opens inline under its card, so the rail stays on the account.
  const railHolding = isMobile ? null : selectedHolding;
  const railHoldingId = railHolding?.id ?? null;

  // Below the two-column band the rail sits under the whole table, so a selection would otherwise
  // change a panel that is off-screen with nothing to tell the user where it went.
  useEffect(() => {
    if (!isStacked || railHoldingId === null) {
      return;
    }
    headingRef.current?.focus();
    headingRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [isStacked, railHoldingId]);

  const hasAnyHolding = counts.open + counts.closed > 0;
  let accountHeading = ACCOUNT_HEADING;
  let subtitle = ACCOUNT_SUBTITLE;
  let placeholder: React.ReactNode = null;
  let message: string | null = null;
  if (isLoading) {
    accountHeading = LOADING_HEADING;
    subtitle = LOADING_SUBTITLE;
    placeholder = (
      <SectionSkeletonRows count={3} label="Loading position context" />
    );
  } else if (hasError) {
    message = ERROR_MESSAGE;
  } else if (!hasAnyHolding) {
    accountHeading = EMPTY_ACCOUNT_HEADING;
    subtitle = EMPTY_ACCOUNT_SUBTITLE;
    placeholder = (
      <SectionEmptyState
        icon={<PieChartOutlineIcon />}
        title={EMPTY_ACCOUNT_TITLE}
        description={EMPTY_ACCOUNT_MESSAGE}
      />
    );
  } else if (counts.filtered === 0) {
    accountHeading = NO_RESULTS_HEADING;
    subtitle = NO_RESULTS_SUBTITLE;
    placeholder = (
      <SectionEmptyState
        icon={<SearchIcon />}
        title={NO_RESULTS_TITLE}
        description={NO_RESULTS_MESSAGE}
      />
    );
  }

  // Holding and display travel as one object so a single narrowing covers both.
  const position = railHolding
    ? {
        holding: railHolding,
        display: buildHoldingDisplay(railHolding),
      }
    : null;
  const coverageGapSummary = coverageGapOf(summary);
  const accountValue = accountValueOf(investment, summary);
  const accountPriceCaption = isPricedValuation(accountValue)
    ? completeThroughOf(investment)
    : undefined;
  const accountCaption = coverageGapSummary
    ? pricedCoverageCaption(coverageGapSummary)
    : captionOf([openPositionsCaption(counts.open), accountPriceCaption]);
  // A pending quote would otherwise dash the hero, reading as a figure on its way rather than one the gap withholds.
  const accountValueText = coverageGapSummary
    ? VALUATION_PLACEHOLDER
    : valuationAmountText(accountValue);

  const heading = position
    ? `${position.holding.instrument.symbol} position insight`
    : accountHeading;
  const headingNote = position ? position.holding.instrument.name : subtitle;

  const ranked = rankedByShareOf(holdings);
  let body: React.ReactNode;
  if (message) {
    body = <Message>{message}</Message>;
  } else if (position) {
    const positionCaption = captionOf([
      `${position.display.quantityText} units`,
      position.display.priceUpdated?.text,
    ]);
    body = (
      <>
        <Hero>
          <HeroLabel>Broker value</HeroLabel>
          <HeroAmount>{position.display.valueText}</HeroAmount>
          <HeroCaption>{positionCaption}</HeroCaption>
        </Hero>
        <MetricGrid metrics={positionMetrics(position.holding)} />
        {ranked ? (
          <ConcentrationList
            title="Position evidence"
            holdings={positionEvidenceOf(position.holding, ranked)}
          />
        ) : (
          <Note>{CONCENTRATION_NOTE}</Note>
        )}
      </>
    );
  } else {
    const concentration = ranked?.slice(0, CONCENTRATION_SIZE) ?? null;
    const isConcentrationWithheld = concentration === null;
    const hasConcentration =
      !isConcentrationWithheld && concentration.length > 0;
    const accountFigures = coverageGapSummary
      ? coverageGapMetrics(coverageGapSummary)
      : accountMetrics(investment, summary);
    body = (
      <>
        <Hero>
          <HeroLabel>Account value</HeroLabel>
          <HeroAmount data-testid="context-rail-value">
            {accountValueText}
          </HeroAmount>
          <HeroCaption>{accountCaption}</HeroCaption>
        </Hero>
        <MetricGrid metrics={accountFigures} />
        {hasConcentration && (
          <ConcentrationList title="Concentration" holdings={concentration} />
        )}
        {isConcentrationWithheld && <Note>{CONCENTRATION_NOTE}</Note>}
      </>
    );
  }

  return (
    <Rail aria-label="Position insight">
      <Panel
        id={CONTEXT_RAIL_PANEL_ID}
        aria-labelledby={CONTEXT_RAIL_HEADING_ID}
      >
        <Header>
          <HeaderText>
            <Heading
              id={CONTEXT_RAIL_HEADING_ID}
              ref={headingRef}
              tabIndex={-1}
            >
              {heading}
            </Heading>
            <HeadingNote>{headingNote}</HeadingNote>
          </HeaderText>
          {railHolding && (
            <ResetButton
              type={ButtonTypes.primary}
              size="small"
              onClick={onClearSelection}
            >
              View account
            </ResetButton>
          )}
        </Header>
        {refreshFailed && (
          <NoticeInset>
            <RefreshNotice
              {...REFRESH_NOTICE_COPY.rail}
              isRetrying={isRefreshing}
              isSecondary
              onRetry={onRetry}
            />
          </NoticeInset>
        )}
        {placeholder ?? <Body>{body}</Body>}
      </Panel>
    </Rail>
  );
};

export const ContextRail = React.memo(ContextRailComponent);
